import * as React from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { adminBrandQuery } from '../../graphql/queries';
import { useState } from 'react';
// @ts-ignore
import nestedProperty from 'nested-property';
import i18n from '@/utils/i18n';
import { updateBrandInvoiceInfoMutation } from '../../graphql/mutations';
// @ts-ignore
import HelpPopover from '../../components/HelpPopover';
import {
  EditItem,
  EditSection,
  LoadingContainer,
} from '../../components/Layout';
import { Button, Alert } from 'reactstrap';
import { ContentSection } from '../../components/Layout/ContentSection';
import { DunningReminderBlockType } from '../../graphql/generatedTypes';
import { DunningReminderBlockStatus } from '../../components/DunningReminderBlockStatus';
import { isNullOrWhitespace } from '@/utils/stringHelper';

type MerchantEditInvoiceSettingsTabProps = {
  externalPickpackId: string | null;
};

export const MerchantEditInvoiceSettingsTab: React.FC<
  MerchantEditInvoiceSettingsTabProps
> = ({ externalPickpackId }) => {
  const [brandUpdate, setBrandUpdate] = useState<any | undefined>(null);
  const [updateBrandInvoice, { loading: updating }] = useMutation(
    updateBrandInvoiceInfoMutation,
  );
  const { data = { brand: null }, loading: loadingBrand } = useQuery(
    adminBrandQuery,
    {
      fetchPolicy: 'no-cache',
      variables: { id: externalPickpackId },
      onCompleted: (brandData) => {
        // @ts-ignore
        setBrandUpdate(brandData.brand);
      },
    },
  );

  // @ts-ignore
  function handleChange({ target }) {
    const { name, value } = target;
    const isNumber = target.hasAttribute('data-isnumber');
    const isDate = target.hasAttribute('data-isdate');
    let formattedValue = value;
    if (isNumber) formattedValue = Number(value);
    if (isDate) {
      try {
        formattedValue = new Date(value).toISOString();
      } catch {}
    }

    setBrandUpdate((oldBrandUpdate: any) => {
      const newBrandUpdate = { ...oldBrandUpdate };
      nestedProperty.set(newBrandUpdate, name, formattedValue);
      return newBrandUpdate;
    });
  }

  function handlePaymentTargetDaysChange(e: any) {
    const value = e.target.value;
    // Strip all non-numeric characters
    const numericValue = value.replace(/\D/g, '');
    setBrandUpdate((oldBrandUpdate: any) => {
      const newBrandUpdate = { ...oldBrandUpdate };
      nestedProperty.set(
        newBrandUpdate,
        'regular_payment_target_days',
        numericValue,
      );
      return newBrandUpdate;
    });
  }

  function handleSave() {
    // Validate dunning reminder block reason if type is PERMANENTLY
    if (
      brandUpdate.dunning_reminder_block_type ===
        DunningReminderBlockType.Permanently &&
      !brandUpdate.dunning_reminder_block_reason?.trim()
    ) {
      alert(i18n.t('Billing:dunningReminderBlockReasonRequired'));
      return;
    }

    const mutationInput: any = {
      address2: {
        ...brandUpdate.address2,
        __typename: undefined,
      },
      monthly_fee: Number(brandUpdate.monthly_fee),
      onboarding_fee_vytal: Number(brandUpdate.onboarding_fee_vytal),
      commission_rate: Number(brandUpdate.commission_rate),
      legal_name: brandUpdate.legal_name,
      prefix: brandUpdate.prefix,
      invoice_email: brandUpdate.invoice_email,
      separate_invoice_each_store: Boolean(
        brandUpdate.separate_invoice_each_store,
      ),
      regular_payment_target_days:
        brandUpdate.regular_payment_target_days &&
        brandUpdate.regular_payment_target_days !== ''
          ? Number(brandUpdate.regular_payment_target_days)
          : null,
      dunning_reminder_block_type: brandUpdate.dunning_reminder_block_type
        ? brandUpdate.dunning_reminder_block_type
        : null,
      dunning_reminder_block_reason:
        brandUpdate.dunning_reminder_block_reason?.trim() || null,
    };

    updateBrandInvoice({
      variables: {
        id: brandUpdate.id,
        brand: mutationInput,
      },
      update: (cache, { data: { updateBrandInvoiceInfo: updatedBrand } }) => {
        setBrandUpdate(updatedBrand);
      },
    }).catch((err) => {
      alert(i18n.t('Merchants:errorUpdatingBrand', { error: err }));
    });
  }

  if (!externalPickpackId) {
    return <p>{i18n.t('Merchants:noExternalPickpackId')}</p>;
  }

  // @ts-ignore
  if (
    loadingBrand ||
    !brandUpdate ||
    data.brand === null ||
    data.brand.id !== brandUpdate.id
  ) {
    return <LoadingContainer />;
  }

  return (
    <ContentSection
      title={i18n.t('Billing:invoiceSettings')}
      collapsable={false}
    >
      <EditSection onSave={handleSave} isLoading={updating}>
        <EditItem title={i18n.t('Billing:invoiceSettings')}>
          <div className="input-group mb-3">
            <span className="input-group-text" id="basic-addon1">
              {i18n.t('Billing:regularPaymentTargetDays')}
            </span>
            <input
              type="text"
              className={'form-control'}
              name="regular_payment_target_days"
              autoComplete="off"
              lang="en-US"
              onChange={handlePaymentTargetDaysChange}
              value={brandUpdate.regular_payment_target_days || ''}
              placeholder="14"
            />
            <HelpPopover
              text={i18n.t('Billing:regularPaymentTargetDaysHelp')}
              id="regular_payment_target_days"
            />
          </div>

          {/* Dunning Reminder Block Status */}
          <DunningReminderBlockStatus
            blockType={data.brand?.dunning_reminder_block_type}
            blockUntil={data.brand?.dunning_reminder_block_until}
            blockReason={data.brand?.dunning_reminder_block_reason}
            translationNamespace="Billing"
            isBlocked={data.brand?.is_dunning_reminder_stopped === true}
          />

          {/* Dunning Reminder Block Change */}
          <div className="input-group mb-3">
            <span className="input-group-text" id="basic-addon1">
              {i18n.t('Billing:dunningReminderBlockType')}
            </span>
            <select
              className="form-control"
              name="dunning_reminder_block_type"
              value={brandUpdate.dunning_reminder_block_type || ''}
              onChange={(e) => {
                const value = e.target.value || null;
                setBrandUpdate({
                  ...brandUpdate,
                  dunning_reminder_block_type: value,
                });
              }}
            >
              <option value="">{i18n.t('Common:selectOption')}</option>
              <option value={DunningReminderBlockType.Permanently}>
                {i18n.t('Billing:dunningReminderBlockTypePermanently')}
              </option>
              <option value={DunningReminderBlockType.Temporary}>
                {i18n.t('Billing:dunningReminderBlockTypeTemporary')}
              </option>
            </select>
          </div>

          {brandUpdate.dunning_reminder_block_type ===
            DunningReminderBlockType.Temporary && (
            <Alert color="info" className="mb-3">
              {i18n.t('Billing:dunningReminderBlockTemporaryInfo')}
            </Alert>
          )}

          <div className="input-group mb-3">
            <span className="input-group-text" id="basic-addon1">
              {i18n.t('Billing:dunningReminderBlockReason')}
            </span>
            <input
              type="text"
              className={
                'form-control' +
                (brandUpdate.dunning_reminder_block_type ===
                  DunningReminderBlockType.Permanently &&
                isNullOrWhitespace(brandUpdate.dunning_reminder_block_reason)
                  ? ' is-invalid'
                  : '')
              }
              name="dunning_reminder_block_reason"
              autoComplete="off"
              onChange={(e) => {
                handleChange(e);
              }}
              value={brandUpdate.dunning_reminder_block_reason || ''}
              placeholder={i18n.t('Billing:dunningReminderBlockReasonHelp')}
            />
            <HelpPopover
              text={i18n.t('Billing:dunningReminderBlockReasonHelp')}
              id="dunning_reminder_block_reason"
            />
          </div>
        </EditItem>
        <Button onClick={handleSave} className="float-end">
          {i18n.t('Billing:saveBrandInvoiceInfo')}
        </Button>
      </EditSection>
    </ContentSection>
  );
};
